import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../../context-store";
import IdNameModel from "../../../models/common/IdNameModel";
import { MOBILE_NUMBER_RULE, FORM_SELECT_RULE } from "../../../utils/validation-utils";
import { Controller, useForm } from "react-hook-form";
import InterviewerService from '../../../services/interviewer/interviewer-service';
import FileUploadService from '../../../services/file-upload-service';
import toasterUtils from "../../../utils/toaster-utils";
import InterviewerPersonalInfoModel from "../../../models/interviewer/response/InterviewerPersonalInfoModel";
import InterviewerPersonalInfoRequestModel from "../../../models/interviewer/request/InterviewerPersonalInfoRequestModel";
import { FileUploadTypeEnum } from "../../../models/enums/FileUploadTypeEnum";
import CandidateService from '../../../services/candidate/candidate-service';
import fileUtils from "../../../utils/file-utils";
import { TextField, Select, MenuItem, Button, FormControl, InputLabel, FormHelperText, FormControlLabel, Checkbox, Autocomplete } from "@mui/material";

interface InterviewerProfileProps {
    objInterviewerProfile: InterviewerPersonalInfoModel;
    experienceList: IdNameModel[];
    cityList: IdNameModel[];
}

const InterviewerPersonalInfoComponent = (props: InterviewerProfileProps) => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [isResumeUploaded, setIsResumeUploaded] = useState<boolean>(false);
    const {
        register,
        setValue,
        getValues,
        handleSubmit,
        control,
        formState: { errors, isValid },
    } = useForm<InterviewerPersonalInfoRequestModel>({
        mode: "onChange",
        defaultValues: props.objInterviewerProfile
    });

    useEffect(() => {
        clearFile();
        if (props.objInterviewerProfile.resumeTitle) {
            setIsResumeUploaded(true);
        }
    }, []);

    const hiddenFileInput: any = React.useRef();
    const browseFileHandler = (event: any) => {
        event.preventDefault();
        hiddenFileInput.current.click();
    };

    const clearFile = () => {
        hiddenFileInput.current.value = "";
    };

    function uploadInterviewerResume(e: any): void {
        const data = new FormData();
        data.append("file", e.target.files[0]);
        data.append("fileName", e.target.files[0].name);
        data.append("userId", loggedInUser ? loggedInUser.profileId : "");
        data.append("fileUploadType", FileUploadTypeEnum.InterviewerResume.toString());
        FileUploadService.uploadFile(data).then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            setIsResumeUploaded(true);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const onSubmit = (formData: InterviewerPersonalInfoRequestModel) => {
        if (!isValid) {
            toasterUtils.showError("Please fill all mandatory fields");
            return;
        }
        if (isResumeUploaded) {
            InterviewerService.saveInterviewerPersonalInfo(formData).then((res) => {
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                toasterUtils.showSuccess(res.informationMessages.toString());
            }).catch((error) => {
                toasterUtils.showError(error);
            });
        } else {
            toasterUtils.showWarning("Please Upload Resume to Save Profile.");
        }
    };

    const downloadInterviewerResume = () => {
        if (loggedInUser) {
            CandidateService.downloadCandidateResume(loggedInUser.profileId, FileUploadTypeEnum.InterviewerResume.toString()).then((res) => {
                fileUtils.downloadFile(res, props.objInterviewerProfile.resumeTitle);
            }).catch((error) => {
                toasterUtils.showError(error);
            });
        }
    }

    return (
        <div className="card card-white">
            <div className="card-body">
                <form className="contact quform" onSubmit={handleSubmit(onSubmit)}>
                    <div className="quform-elements">
                        <div className="row">
                            <div className="col-md-6">
                                <Controller name="cityId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                    render={({ field }) => (
                                        <Autocomplete {...field} value={props.cityList.find(x => x.id === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.cityList} getOptionLabel={(option) => option.name || ""}
                                            renderInput={(params) => (
                                                <TextField {...params} label="City" size="small" required margin="normal" fullWidth error={!!errors.cityId} helperText={errors.cityId?.message} />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-md-6">
                                <Controller name="experienceId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                    render={({ field }) => (
                                        <Autocomplete {...field} value={props.experienceList.find(x => x.id === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.experienceList} getOptionLabel={(option) => option.name || ""}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Experience" size="small" required margin="normal" fullWidth error={!!errors.experienceId} helperText={errors.experienceId?.message} />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <FormControl fullWidth margin="normal" error={!!errors.mobileNumber}>
                                    <TextField type="text" label="Mobile Number" size="small" {...register("mobileNumber", MOBILE_NUMBER_RULE)} error={!!errors.mobileNumber}
                                        helperText={errors.mobileNumber?.message} autoComplete="off" required />
                                </FormControl>
                            </div>
                            <div className="col-md-6">
                                <FormControl fullWidth margin="normal" error={!!errors.address}>
                                    <TextField label="Address" multiline rows={3} size="small"
                                        {...register("address")} error={!!errors.address} helperText={errors.address?.message} autoComplete="off" />
                                </FormControl>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-3">
                                <FormControl fullWidth margin="normal">
                                    <InputLabel shrink>Resume {!isResumeUploaded && <span className="required">*</span>}</InputLabel>
                                    <Button variant="outlined" component="label" size="small" fullWidth sx={{ mt: 1 }}                                >
                                        Upload Resume
                                        <input type="file" hidden ref={hiddenFileInput} onChange={uploadInterviewerResume} accept=".doc,.docx,application/pdf" />
                                    </Button>
                                    {!isResumeUploaded && (
                                        <FormHelperText error>
                                            Please upload your resume.
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                            {isResumeUploaded &&
                                <div className="col-md-3 mt-4">
                                    <Button variant="contained" color="primary" startIcon={<i className="fa fa-download"></i>} onClick={downloadInterviewerResume} size="small"                                >
                                        Download Resume
                                    </Button>
                                </div>
                            }
                        </div>
                        <div className="row">
                            <div className="col-md-9"></div>
                            <div className="col-md-3 text-end">
                                <Button variant="contained" color="primary" type="submit" disabled={!isValid}>
                                    Save Profile
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default InterviewerPersonalInfoComponent;